/**
 * The two Monday reports — Cocon Requests (screen 09) and Cocon Uploads Tracking (11).
 *
 * ⚠ **The tiles here are pure reads of the board's CURRENT state.** Ubi dropped the
 * per-week dimension from tile 2 on 16 Sep 2026. The week-by-week figures she asked for on
 * 26 Oct 2026 (closed per week, pending per partner, the trend) are NOT here: they are rebuilt
 * from the board's activity log every morning — `request-history.ts`. ⚠ This file once said
 * Monday keeps no history; it does. The status column has no closed-at date, but the
 * activity log holds every change back to the board's first day.
 *
 * ⚠ **The vocabulary is the board's, never the spreadsheet's.** `Pending action by Partner`
 * has a lower-case "a"; the spreadsheet capitalised it. `Upload Planned` has a capital P;
 * the spreadsheet did not. Where they disagree the board wins, because the board is what
 * the API returns and a mismatched label silently counts zero.
 *
 * Everything here is a pure function over plain items, so all of it is tested without a
 * network — which matters, because these numbers go to the companies they describe.
 */
// ─────────────────────────────────────────────────────────────────────────────
// The boards
// ─────────────────────────────────────────────────────────────────────────────
export const MONDAY_BOARDS = {
    requests: { id: "1982041654", name: "Cocon requests" },
    uploads: { id: "2073893219", name: "Cocon Uploads Tracking" },
};
/**
 * The column ids, verified against the live boards.
 *
 * ⚠ Ids, never titles. A title is editable by anyone with the board open — and one of
 * these is already `"Current  Iteration"` with a double space, which is exactly the kind of
 * thing a title-based lookup breaks on silently.
 */
export const REQUEST_COLUMNS = {
    pop: "dropdown_mktc92j9",
    createdAt: "creation_log",
    type: "status_11",
    priority: "status_1",
    status: "color_mkrs6qz4",
    company: "color_mkrsde15",
    drawingVersion: "text_mktcb67p",
};
export const UPLOAD_COLUMNS = {
    /** Titled "Date of FIRST HP Upload" until Ubi renamed it in Oct 2026. Same id, same data. */
    hpPlannedUpload: "date_mktczk1y",
    /**
     * Created by Ubi in Oct 2026, filled once on 23 Oct from DMP's `DEFAULT_INSTALLDATE`.
     * ⚠ For a reworked drawing that is the date of the LAST upload, not the first.
     */
    uploadedToCocon: "date_mm8dxj77",
    pop: "dropdown_mkv66t5w",
    version: "text_mktc7e21",
    fileStatus: "color_mkta1bkp",
    acceptance: "color_mktark7m",
    iteration: "color_mktjk3v1",
    ufAcceptance: "date_mkta7my9",
    reworkDue: "date_mktant6y",
    company: "dropdown_mkta4gv1",
};
/**
 * ⚠ THE definition of closed, and there is exactly one: the status is `Done`.
 *
 * Ubi, 16 Sep 2026: *"we do not want the numbers per week but the open and closed tickets
 * based on status done or not done"*. Every other status — including `On hold`, which is
 * 7% of the board — is open. A ticket nobody is working on is still not finished.
 */
export const CLOSED_STATUS = "Done";
export function isOpen(ticket) {
    return ticket.status !== CLOSED_STATUS;
}
/**
 * The six non-Done statuses, in the order tile 4 shows them as columns.
 *
 * ⚠ **The spreadsheet named four of these; the board has six.** Leaving `On hold` (183)
 * and `Quality issue` (4) out would count 187 open tickets nowhere while the tile claims to
 * show the open backlog — 12% of it, and Constructel alone holds most of the On hold ones.
 * Question 1 in the spec; built with all six and flagged on screen, so the tile reconciles
 * to the open total instead of quietly disagreeing with tile 2.
 */
export const OPEN_STATUS_COLUMNS = [
    "Pending action by Partner",
    "Pending UF review",
    "Working on it",
    "with IT helpdesk",
    "On hold",
    "Quality issue",
];
/**
 * Ubi's working order for the groups, from the spreadsheet — not sorted by size.
 *
 * ⚠ Anything not in this list is appended after it rather than dropped. The board has nine
 * groups with items and the spreadsheet lists eight: `ODF survey pending` (18 tickets, all
 * Unifiber) is missing from hers. Question 2 in the spec; dropping 18 real tickets to match
 * a list is the wrong way round, so they appear last and the screen says they are extra.
 */
export const GROUP_ORDER = [
    "Design changes",
    "Maintenance",
    "Pre-engineering",
    "Order resolution",
    "HC requests",
    "Cocon errors",
    "Greenfields",
    "Service request",
];
export function byGroupOrder(a, b) {
    const ia = GROUP_ORDER.indexOf(a);
    const ib = GROUP_ORDER.indexOf(b);
    if (ia === -1 && ib === -1)
        return a.localeCompare(b);
    if (ia === -1)
        return 1;
    if (ib === -1)
        return -1;
    return ia - ib;
}
/** A group the spreadsheet never named. Flagged rather than hidden. */
export function isUnlistedGroup(group) {
    return !GROUP_ORDER.includes(group);
}
/** Count by an extracted key, biggest first, `null` collected under `fallback`. */
export function tallyBy(items, key, fallback = "—") {
    const counts = new Map();
    for (const item of items) {
        const value = key(item) || fallback;
        counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return [...counts.entries()]
        .map(([label, n]) => ({ label, n }))
        .sort((a, b) => b.n - a.n || a.label.localeCompare(b.label));
}
/** Split a tally by company, so the accordion under each pie is real rather than decorative. */
export function splitByCompany(tickets, key, fallback = "—") {
    const rows = new Map();
    for (const ticket of tickets) {
        const label = key(ticket) || fallback;
        let row = rows.get(label);
        if (!row) {
            row = { label, total: 0, byCompany: {} };
            rows.set(label, row);
        }
        row.total++;
        const company = ticket.company || fallback;
        row.byCompany[company] = (row.byCompany[company] ?? 0) + 1;
    }
    return [...rows.values()].sort((a, b) => b.total - a.total || a.label.localeCompare(b.label));
}
/**
 * The company a ticket belongs to, by the SAME mapping that scopes a contractor's rows.
 *
 * Ubi, Sep 2026: a Company column in tile 4 whose filter lists *"all companies created in
 * the admin menu"*. So `CIF - Circet` and `CIF-Circet` both read Circet — the name the
 * administrator created — and a label no company claims yet (Jacops, S30…) is shown as the
 * board spells it rather than dropped: those tickets exist whether or not anyone has set
 * their company up. `null` only where the board itself has no company.
 */
export function companyForLabel(label, companies) {
    if (label === null)
        return null;
    // One label can belong to one company at most — the Companies screen refuses a second
    // claim (hard rule 28) — so the first match is the only match.
    return companies.find((c) => c.labels.includes(label))?.name ?? label;
}
/**
 * ⚠ The company is part of the row's key, so a group × POP worked by two companies is two
 * rows. One row with both companies' counts summed would have a Company cell that is true
 * of neither.
 */
export function groupPopRows(tickets, companies = []) {
    const rows = new Map();
    for (const ticket of tickets) {
        if (!isOpen(ticket))
            continue;
        const company = companyForLabel(ticket.company, companies);
        const key = `${ticket.group}\u0000${ticket.pop ?? ""}\u0000${company ?? ""}`;
        let row = rows.get(key);
        if (!row) {
            row = { group: ticket.group, pop: ticket.pop, company, byStatus: {}, total: 0 };
            rows.set(key, row);
        }
        row.byStatus[ticket.status] = (row.byStatus[ticket.status] ?? 0) + 1;
        row.total++;
    }
    return sortGroupPopRows([...rows.values()]);
}
/**
 * ⚠ ONE filter function, used by every tile and the KPI row.
 *
 * The same discipline as `visibleRows` on Order Activation (hard rule 9): if a tile
 * filtered for itself, two tiles on one screen would eventually answer the same question
 * differently — which on this product is the whole failure mode.
 */
export function filterTickets(tickets, filters) {
    const needle = filters.search?.trim().toLowerCase();
    const groups = filters.groups?.length ? new Set(filters.groups) : null;
    const pops = filters.pops?.length ? new Set(filters.pops) : null;
    return tickets.filter((ticket) => {
        if (filters.company && ticket.company !== filters.company)
            return false;
        if (groups && !groups.has(ticket.group))
            return false;
        if (pops && !pops.has(ticket.pop ?? ""))
            return false;
        if (needle) {
            const haystack = `${ticket.name} ${ticket.group} ${ticket.pop ?? ""} ${ticket.drawingVersion ?? ""}`;
            if (!haystack.toLowerCase().includes(needle))
                return false;
        }
        return true;
    });
}
export function summariseRequests(tickets) {
    const open = tickets.filter(isOpen);
    return {
        total: tickets.length,
        open: open.length,
        closed: tickets.length - open.length,
        byStatus: tallyBy(tickets, (t) => t.status),
        byGroup: tallyBy(tickets, (t) => t.group),
        byCompany: tallyBy(tickets, (t) => t.company),
        byPriority: tallyBy(tickets, (t) => t.priority),
        withoutCompany: tickets.filter((t) => !t.company).length,
        openWithoutPop: open.filter((t) => !t.pop).length,
    };
}
/**
 * The five acceptance labels, in workflow order — not by size.
 *
 * ⚠ Ubi's v2 spreadsheet writes "Upload planned" with a lower-case p. The board writes
 * `Upload Planned`. The board wins: it is what the API returns, and a label that does not
 * match counts zero without any error anywhere.
 */
export const ACCEPTANCE_ORDER = [
    "with HP Partner",
    "with Yungo",
    "Pending UF Approval",
    "Upload Planned",
    "Accepted",
];
export const ACCEPTED = "Accepted";
export const UPLOAD_PLANNED = "Upload Planned";
export function acceptanceMatrix(items, fallback = "No company") {
    const rows = new Map();
    for (const item of items) {
        const company = item.company || fallback;
        let row = rows.get(company);
        if (!row) {
            row = { company, byStatus: {}, total: 0 };
            rows.set(company, row);
        }
        const status = item.acceptance || "—";
        (row.byStatus[status] ??= []).push(item);
        row.total++;
    }
    // ⚠ The "no company" row sorts LAST but is never dropped. 7 of 170 items have none, so
    // tile 1 (which counts by status) and this tile would otherwise disagree by 7 with
    // nothing on screen to explain it.
    return [...rows.values()].sort((a, b) => {
        if (a.company === fallback)
            return 1;
        if (b.company === fallback)
            return -1;
        return b.total - a.total || a.company.localeCompare(b.company);
    });
}
/**
 * Tile 3: rework overdue — not accepted, and the rework due date is in the past.
 *
 * ⚠ `today` is passed in, never read from the clock here. Same reason as hard rule 26: a
 * date compared against `new Date()` at render time ticks over at midnight, so the same
 * data gives two different answers either side of it, and two people comparing screens see
 * two different numbers.
 */
export function reworkOverdue(items, today) {
    return items.filter((item) => item.acceptance !== ACCEPTED && item.reworkDueDate !== null && item.reworkDueDate < today);
}
/** How many items carry a rework due date at all — the ceiling on what tile 3 can see. */
export function withReworkDue(items) {
    return items.filter((item) => item.reworkDueDate !== null).length;
}
/**
 * ⚠ Written out rather than taken from `toLocaleDateString`.
 *
 * `en-GB` renders September as **"Sept"** — four letters where every other month gives
 * three, which in a row of column headers looks like a mistake. Worse, `Intl` output
 * depends on the ICU build, so the same code can label a column differently on two machines
 * — and this product exists so that two people see the same thing.
 */
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function plannedUploadBuckets(items, from) {
    const planned = items.filter((item) => item.acceptance === UPLOAD_PLANNED);
    const start = new Date(`${from.slice(0, 7)}-01T00:00:00Z`);
    const months = [];
    for (let i = 0; i < 6; i++) {
        const month = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + i, 1));
        months.push({
            key: `${month.getUTCFullYear()}-${String(month.getUTCMonth() + 1).padStart(2, "0")}`,
            label: `${MONTH_ABBR[month.getUTCMonth()]} ${month.getUTCFullYear()}`,
            items: [],
        });
    }
    const first = months[0].key;
    const last = months[months.length - 1].key;
    const overdue = { key: "overdue", label: "Overdue", items: [] };
    const later = { key: "later", label: "Later", items: [] };
    const undated = { key: "no-date", label: "No date", items: [] };
    for (const item of planned) {
        if (!item.hpPlannedUploadDate) {
            undated.items.push(item);
            continue;
        }
        const month = item.hpPlannedUploadDate.slice(0, 7);
        if (month < first)
            overdue.items.push(item);
        else if (month > last)
            later.items.push(item);
        else
            months.find((m) => m.key === month)?.items.push(item);
    }
    // ⚠ `No date` appears only when it has something in it. Today every one of the 26 has a
    // date, and an always-visible empty column would read as a defect rather than as zero.
    return [overdue, ...months, later, ...(undated.items.length > 0 ? [undated] : [])];
}
export function summariseUploads(items, today) {
    return {
        total: items.length,
        byAcceptance: tallyBy(items, (i) => i.acceptance),
        byFileStatus: tallyBy(items, (i) => i.fileStatus),
        // ⚠ Iterations sort by NAME, not by size: IT1…IT7 is a sequence, and a bar chart of it
        // ordered by count tells you nothing about whether drawings are getting worse.
        byIteration: tallyBy(items, (i) => i.iteration).sort((a, b) => a.label.localeCompare(b.label)),
        byCompany: tallyBy(items, (i) => i.company),
        withoutCompany: items.filter((i) => !i.company).length,
        withReworkDue: withReworkDue(items),
        overdue: reworkOverdue(items, today).length,
        planned: items.filter((i) => i.acceptance === UPLOAD_PLANNED).length,
    };
}
export function filterUploads(items, filters) {
    const needle = filters.search?.trim().toLowerCase();
    return items.filter((item) => {
        if (filters.companyUnset && item.company !== null)
            return false;
        if (filters.company && item.company !== filters.company)
            return false;
        if (filters.acceptance && item.acceptance !== filters.acceptance)
            return false;
        if (needle) {
            // ⚠ The company is in here because it is a column on screen. A search box beside a
            // table is read as "search this table", and one that silently skips a visible column
            // teaches people it is broken.
            const haystack = `${item.name} ${item.pop ?? ""} ${item.version ?? ""} ${item.company ?? ""}`;
            if (!haystack.toLowerCase().includes(needle))
                return false;
        }
        return true;
    });
}
// ─────────────────────────────────────────────────────────────────────────────
// Scoping — who may see which rows
// ─────────────────────────────────────────────────────────────────────────────
/**
 * ⚠ THE Monday scoping rule, and it is deliberately the narrowest one that can be written.
 *
 * A contractor sees only the rows whose company label is one of the labels **explicitly
 * mapped to their own company row** (`Company.mondayCompanyLabels`). That array exists
 * because one company is spelled several ways across systems: Circet is `CIF - Circet` with
 * spaces on the requests board and `CIF-Circet` without on the uploads board, and `circet`
 * in DMP.
 *
 * ⚠ **It does NOT include subcontractors, and that is open question 4, not an oversight.**
 * Whether Circet's view should contain `S30`, `Walfit` or `Jacops` is the difference between
 * a contractor seeing their own work and seeing a competitor's, and only Ubi can answer it.
 * Until she does, the safe answer is the narrow one — the same instinct as a new drawing
 * defaulting to hidden (hard rule 8). Widening it later is one row in the database;
 * unseeing a competitor's backlog is not possible.
 *
 * ⚠ A row with NO company reaches internal users only, exactly as an unassigned DMP order
 * does. It belongs to nobody, so no contractor can claim it.
 */
export function matchesMondayScope(rowCompany, labels) {
    if (labels === null)
        return true;
    if (rowCompany === null)
        return false;
    return labels.includes(rowCompany);
}
export function visibleTickets(tickets, labels) {
    if (labels === null)
        return [...tickets];
    return tickets.filter((ticket) => matchesMondayScope(ticket.company, labels));
}
export function visibleUploads(items, labels) {
    if (labels === null)
        return [...items];
    return items.filter((item) => matchesMondayScope(item.company, labels));
}
/**
 * Deliberate departures from Monday's palette, each with its reason.
 *
 * ⚠ Kept apart from the live colours rather than merged into them, so "what Monday says"
 * and "what we chose" never become indistinguishable.
 */
export const COLOUR_OVERRIDES = {
    iteration: {
        // Monday paints IT1 grey (#c4c4c4). Grey reads as "inactive" or "no value" everywhere
        // else in this application, and IT1 is neither — it is the good case, a drawing
        // accepted first time. Ubi asked for royal blue, 16 Sep 2026.
        IT1: "#4169e1",
    },
};
export function labelColour(colours, group, label) {
    return COLOUR_OVERRIDES[group]?.[label] ?? colours[group]?.[label] ?? "#94a3b8";
}
export function tint(hex) {
    const parsed = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
    const value = parsed ? parseInt(parsed[1], 16) : 0x94a3b8;
    const r = (value >> 16) & 255;
    const g = (value >> 8) & 255;
    const b = value & 255;
    const max = Math.max(r, g, b) / 255;
    const min = Math.min(r, g, b) / 255;
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    let h = 0;
    if (d !== 0) {
        const rn = r / 255;
        const gn = g / 255;
        const bn = b / 255;
        if (max === rn)
            h = ((gn - bn) / d) % 6;
        else if (max === gn)
            h = (bn - rn) / d + 2;
        else
            h = (rn - gn) / d + 4;
        h *= 60;
        if (h < 0)
            h += 360;
    }
    const hsl = (sat, light) => `hsl(${Math.round(h)} ${Math.round(sat * 100)}% ${Math.round(light)}%)`;
    return {
        bg: hsl(Math.min(s, 0.7), 96),
        border: hsl(Math.min(s, 0.7), 84),
        // ⚠ Lightness is capped so the text stays readable even from a pale source like the
        // #a1e3f6 Monday gives "Order resolution".
        text: hsl(Math.min(s + 0.1, 0.75), Math.min(l * 100 * 0.55, 34)),
        solid: hex,
    };
}
export const GROUP_POP_RESERVED_FIELDS = ["group", "pop", "company", "total"];
/**
 * Which way a column sorts when you first click it.
 *
 * ⚠ A count sorts DESCENDING first and a name ASCENDING first, because that is what the
 * click means: "which POPs have the most tickets waiting on the partner" is the question,
 * and answering it with the zeroes at the top would take a second click every time.
 */
export function defaultSortDirection(field) {
    return field === "group" || field === "pop" || field === "company" ? "asc" : "desc";
}
/**
 * Sort tile 4.
 *
 * ⚠ **The direction applies to the primary key only.** Ties break on the natural secondary
 * — group order, then POP, then company — in their own ascending order whichever way the
 * primary runs. Reversing the tie-break as well would shuffle rows the reader did not ask to
 * move, and makes a descending sort look like a different table rather than the same one
 * upside down.
 *
 * ⚠ **A row with no POP, or no company, sorts LAST in both directions.** It is a real row
 * (14 open tickets on the real board have no POP), but "no value" is not a value: floating
 * it to the top of a descending sort would put the board's data-entry gap above the work.
 *
 * `group` ascending reproduces the default order exactly — Ubi's group order, then POP, then
 * company — so clearing a sort and never having sorted are the same thing.
 */
export function sortGroupPopRows(rows, field = "group", direction = "asc") {
    const sign = direction === "desc" ? -1 : 1;
    /** Ascending with nulls last — or, for the primary key, `sign`-ed with nulls STILL last. */
    const text = (a, b, s = 1) => {
        if (a === b)
            return 0;
        if (a === null)
            return 1;
        if (b === null)
            return -1;
        return s * a.localeCompare(b);
    };
    const byPop = (a, b) => text(a.pop, b.pop);
    const byCompany = (a, b) => text(a.company, b.company);
    const byGroup = (a, b) => byGroupOrder(a.group, b.group);
    const valueOf = (row) => (field === "total" ? row.total : (row.byStatus[field] ?? 0));
    return [...rows].sort((a, b) => {
        if (field === "group")
            return sign * byGroup(a, b) || byPop(a, b) || byCompany(a, b);
        if (field === "pop")
            return text(a.pop, b.pop, sign) || byGroup(a, b) || byCompany(a, b);
        if (field === "company")
            return text(a.company, b.company, sign) || byGroup(a, b) || byPop(a, b);
        return sign * (valueOf(a) - valueOf(b)) || byGroup(a, b) || byPop(a, b) || byCompany(a, b);
    });
}
/**
 * ⚠ **A missing value sorts LAST in both directions, always.**
 *
 * The board is full of them: 14 open tickets with no POP, 7 upload requests with no company,
 * 113 of 170 with no rework due date. They are real rows and must stay findable, but "not
 * filled in" is not a value — floating the board's data-entry gaps above the work on every
 * descending sort would make the top of every table useless.
 *
 * Numbers compare numerically, strings with `localeCompare`, and a mixed column compares as
 * strings so it cannot throw.
 */
export function compareSortable(a, b, direction = "asc") {
    if (a === null && b === null)
        return 0;
    if (a === null)
        return 1;
    if (b === null)
        return -1;
    const sign = direction === "desc" ? -1 : 1;
    if (typeof a === "number" && typeof b === "number")
        return sign * (a - b);
    return sign * String(a).localeCompare(String(b));
}
/**
 * Sort rows by one accessor.
 *
 * ⚠ **Stable**, so rows the comparator calls equal keep the order they arrived in. That is
 * what makes a sort on a column of mostly-equal values — "Acceptance", say, where a third
 * of the table reads `with HP Partner` — look like the same table reordered rather than
 * reshuffled at random.
 */
export function sortRowsBy(rows, value, direction = "asc") {
    return [...rows].sort((a, b) => compareSortable(value(a), value(b), direction));
}
/** A count opens biggest-first; a name opens A-to-Z. See `defaultSortDirection`. */
export function defaultDirectionForValue(sample) {
    return typeof sample === "number" ? "desc" : "asc";
}
//# sourceMappingURL=monday.js.map